import React, { useState } from 'react'
import { useKey } from 'react-use'
import { Route } from 'react-router'

import { routes, useRegisterCommandBarCommands } from '@sketch/modules-common'
import { shortcuts, keyWithoutModifier } from '@sketch/utils'

import {
  useSortingSettings,
  useSyncSortSettings,
} from '../../hooks/useSortSettings'

import { Filters, SearchBar } from '../SearchBar'
import { SortDropdown } from '../SortDropdown'
import {
  Flex,
  NavigationSelector,
  Tooltip,
  useResponsiveDropdown,
  useSearchExpanded,
  useMobileScrolled,
  useForDesktop,
} from '@sketch/components'
import { ReactComponent as BulletList } from '@sketch/icons/bullet-list-24'
import { ReactComponent as SquareGrid } from '@sketch/icons/square-grid-24'

// GraphQL
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
// Here DocumentLayout are used in non exhaustive way
// therefore build should not break after SketchQL deployments
// eslint-disable-next-line no-restricted-imports
import { DocumentLayout } from '@sketch/gql-types/expansive'

import {
  NavbarWrapper,
  NavbarSection,
  Divider,
  SortIcon,
  SortButton,
} from './ShareNavbar.styles'
import { useDocumentLayout } from './ShareNavbar.hooks'

const DOCUMENT_LAYOUT = [
  {
    type: 'icon' as const,
    label: 'Grid Layout',
    value: 'GRID' as const,
    icon: SquareGrid,
    shortcut: shortcuts.gridView,
  },
  {
    type: 'icon' as const,
    label: 'List Layout',
    value: 'LIST' as const,
    icon: BulletList,
    shortcut: shortcuts.listView,
  },
]

const SortSettings: React.FC<{ showDivider: boolean }> = ({ showDivider }) => {
  const [hoveringButton, setHoveringButton] = useState(false)

  const [sortDropdownContent, sortDropdownButtonProps] = useResponsiveDropdown({
    dropdown: SortDropdown,
    placement: 'bottom',
    hide: ['clickedOutsideContent'],
    usePortal: true,
  })

  const { sortLabel } = useSortingSettings()
  useSyncSortSettings()

  return (
    <Route
      path={[
        routes.WORKSPACE_SHARES.template(),
        routes.WORKSPACE_DRAFTS.template(),
        routes.WORKSPACE_LIBRARIES.template(),
        routes.WORKSPACE_SHARED_WITH_ME.template(),
        routes.WORKSPACE_TEMPLATES.template(),
        routes.WORKSPACE_PROJECT.template(),
      ]}
    >
      <Flex
        mr={2}
        onMouseEnter={() => setHoveringButton(true)}
        onMouseLeave={() => setHoveringButton(false)}
      >
        <Tooltip
          content={`Sort by: ${sortLabel}`}
          placement="bottom"
          visible={hoveringButton && !sortDropdownButtonProps['aria-expanded']}
          disableWhenTouchDevice
        >
          <SortButton
            variant="ghost"
            size="32"
            data-testid="sort-dropdown"
            {...sortDropdownButtonProps}
          >
            <SortIcon />
          </SortButton>
        </Tooltip>
      </Flex>
      {sortDropdownContent}
      {showDivider && <Divider />}
    </Route>
  )
}

interface ShareNavbarProps {
  userRole: WorkspaceMinimalFragment['userRole']
  title?: React.ReactNode
  showViewOptions?: boolean
  showSearch?: boolean
}

const ShareNavbar: React.FC<ShareNavbarProps> = props => {
  const { userRole, title, showViewOptions = true, showSearch = true } = props
  const isDesktopAndBigger = useForDesktop()
  const mobileScrolled = useMobileScrolled()

  const { documentsLayout, updateDocumentLayout } = useDocumentLayout()

  const { searchExpanded, setSearchExpanded } = useSearchExpanded()

  /**
   * Manager shortcuts:
   * - Search (/)
   * - View as Grid ([)
   * - View as List (])
   */
  useKey(keyWithoutModifier(shortcuts.search), function () {
    const inputElement = document!.querySelector(
      'input[name="search"]'
    ) as HTMLInputElement

    if (!searchExpanded) {
      setSearchExpanded(true)
    }

    // Needed to fire `focus` after the keydown, that avoids filling the search
    // with `/`
    setTimeout(function () {
      inputElement?.focus()
    }, 0)
  })

  useKey(
    keyWithoutModifier([shortcuts.gridView, shortcuts.listView]),
    function (event) {
      const key = event.key as
        | typeof shortcuts.gridView
        | typeof shortcuts.listView
      const viewMode = DOCUMENT_LAYOUT.find(layout => layout.shortcut === key)

      if (!viewMode) {
        return
      }

      updateDocumentLayout(viewMode.value as DocumentLayout)
    }
  )

  useRegisterCommandBarCommands(
    'display-options',
    () => {
      if (!documentsLayout) {
        return []
      }

      const toggleValue: DocumentLayout =
        documentsLayout === 'GRID' ? 'LIST' : 'GRID'

      const itemDetails = DOCUMENT_LAYOUT.find(v => v.value === toggleValue)
      if (!itemDetails) {
        return []
      }

      return [
        {
          id: 'display-options.as-list',
          analyticsId: 'display-options.as-list',
          kind: 'command',
          title: `Show as ${itemDetails?.label}`,
          shortcut: itemDetails?.shortcut,
          section: 'Current View',
          keywords: ['view', 'mode', itemDetails.label],
          icon: itemDetails?.icon,
          priority: 25,
          run: () => updateDocumentLayout(toggleValue),
        },
      ]
    },
    [documentsLayout, updateDocumentLayout]
  )

  const mobile = (
    <NavbarWrapper>
      <NavbarSection align="start">
        {mobileScrolled ? title : null}
      </NavbarSection>
      {showViewOptions ? (
        <NavbarSection align="end">
          <NavigationSelector
            name="document layout"
            items={DOCUMENT_LAYOUT}
            selected={documentsLayout}
            hideTooltip={true}
            onChange={({ value }) => {
              updateDocumentLayout(value as DocumentLayout)
            }}
          />
          <SortSettings showDivider={!showSearch} />
          {!showSearch && (
            <Filters userCanToggleProjects={userRole !== 'GUEST'} />
          )}
        </NavbarSection>
      ) : null}
    </NavbarWrapper>
  )

  return !isDesktopAndBigger ? (
    mobile
  ) : (
    <NavbarWrapper>
      <NavbarSection align="end">
        {showViewOptions ? (
          <>
            <NavigationSelector
              name="document layout"
              items={DOCUMENT_LAYOUT}
              selected={documentsLayout}
              hideTooltip={false}
              onChange={({ value }) => {
                updateDocumentLayout(value as DocumentLayout)
              }}
            />

            <SortSettings showDivider />
          </>
        ) : null}

        <Filters userCanToggleProjects={userRole !== 'GUEST'} />
      </NavbarSection>

      {showSearch && (
        <NavbarSection align="end" style={{ flexBasis: 'auto' }}>
          <SearchBar />
        </NavbarSection>
      )}
    </NavbarWrapper>
  )
}

export default ShareNavbar
